import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { MEMBERSHIP_CONFIGURATION_RESOURCE } from 'src/features/membership/api/useGetMembershipConfiguration';
import { LIST_MEMBERSHIP_ZONE_RULES_RESOURCE } from 'src/features/membership/api/zoneRule/useListMembershipZoneRules';
import { useUpdateMembershipZoneRule } from 'src/features/membership/api/zoneRule/useUpdateMembershipZoneRule';
import MembershipZoneRuleFormModal from 'src/features/membership/components/forms/zoneRule/MembershipZoneRuleFormModal';
import { ZoneRule } from 'src/features/membershipType/types';
import { zoneRulesMessages } from 'src/features/membershipType/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface UpdateZoneRuleFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedZoneRule: ZoneRule;
    children?: React.ReactNode;
}

function UpdateMembershipZoneRuleFormModal<TData>({
    className,
    children,
    selectedZoneRule,
}: UpdateZoneRuleFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { membershipId = '' } = useParams();
    const { parkingId } = useParking();

    const selectedZoneRuleTranslation =
        selectedZoneRule?.zone?.translations?.find((translation) => translation.language === locale)?.name || '';

    const { mutate, isPending } = useUpdateMembershipZoneRule({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIP_ZONE_RULES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_CONFIGURATION_RESOURCE] });

            toast({
                title: formatMessage(zoneRulesMessages.updateZoneRuleSuccess),
                description: formatMessage(zoneRulesMessages.updateZoneRuleSuccessDescription, {
                    entry: success.zone?.translations?.find((translation) => translation.language === locale)?.name,
                }),
            });
        },
    });

    return (
        <MembershipZoneRuleFormModal
            selectedZoneRule={selectedZoneRule}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ membershipId, parkingId, ruleId: selectedZoneRule.id, ...data }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, {
                entry: formatMessage(zoneRulesMessages.rule).toLowerCase(),
            })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(zoneRulesMessages.editZoneRuleSubtitle, {
                entry: selectedZoneRuleTranslation,
            })}
        >
            {children}
        </MembershipZoneRuleFormModal>
    );
}

export default UpdateMembershipZoneRuleFormModal;
